import type { MouseEventHandler, ReactNode } from "react";
import { Link } from "react-router-dom";
import "./NavTabs.css";

export interface NavTabProps {
  /** route to open; leave out for a tab that only runs onClick */
  to?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  active?: boolean;
  /** number after the label, e.g. pending requests */
  count?: number;
  id?: string;
  className?: string;
  children: ReactNode;
}

/** One link in the top bar. Use inside <NavTabs>. */
export const NavTab = ({
  to,
  onClick,
  active = false,
  count,
  id,
  className = "",
  children,
}: NavTabProps) => (
  <Link
    id={id}
    to={to ?? "#"}
    onClick={(e) => {
      if (!to) e.preventDefault();
      onClick?.(e);
    }}
    className={`ds-nav-tab ${active ? "is-active" : ""} ${className}`.trim()}
    aria-current={active ? "page" : undefined}
  >
    {children}
    {count !== undefined && count > 0 && (
      <span className="ds-nav-tab__count">{count}</span>
    )}
  </Link>
);

export interface NavTabsProps {
  /** label for screen readers */
  label?: string;
  className?: string;
  children: ReactNode;
}

/**
 * Top bar navigation: a row of page links, the current one highlighted.
 * Example: <NavTabs><NavTab to="/home" active>Properties</NavTab></NavTabs>
 */
const NavTabs = ({ label, className = "", children }: NavTabsProps) => (
  <nav className={`ds-nav-tabs ${className}`.trim()} aria-label={label}>
    {children}
  </nav>
);

export default NavTabs;
